/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { logger } from "@/lib/utils/logger";
import { useEffect, useState } from "react";
import { useUIPreferences } from "./use-user-preferences";

export interface MetricPreferences {
  /**
   * Array of metric IDs to display, in order
   * Empty array means use all available metrics
   */
  visibleMetrics?: string[];
  /**
   * Mapping of slot positions to metric IDs (for custom layouts)
   * Key is slot index (0, 1, 2, etc.), value is metric ID
   */
  slotMapping?: Record<number, string>;
}

export function useMetricPreferences(dashboardId?: string) {
  const { ui, updateUI } = useUIPreferences();
  const [serverPrefs, setServerPrefs] = useState<MetricPreferences | null>(
    null,
  );
  const isReady = Boolean(dashboardId);

  useEffect(() => {
    if (!dashboardId) return;
    // Access metrics from UIPreferences (similar to tables)
    const metrics = ui?.metrics as
      | Record<string, MetricPreferences>
      | undefined;
    if (metrics && metrics[dashboardId]) {
      setServerPrefs(metrics[dashboardId] ?? null);
    } else {
      setServerPrefs(null);
    }
  }, [ui, dashboardId]);

  const savePrefs = async (partial: MetricPreferences) => {
    if (!dashboardId) return;
    try {
      const current =
        (ui?.metrics as Record<string, MetricPreferences> | undefined) ?? {};
      const nextForDashboard: MetricPreferences = {
        ...(current[dashboardId] ?? {}),
        ...partial,
      };
      const next = { ...current, [dashboardId]: nextForDashboard };
      await updateUI({ ...ui, metrics: next });
      setServerPrefs(nextForDashboard);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      logger.error("Failed to save metric preferences:", {
        error: errorMessage,
        dashboardId,
        details: error instanceof Error ? error.stack : undefined,
      });
      throw error; // Re-throw to allow callers to handle
    }
  };

  return {
    serverPrefs,
    savePrefs,
    isReady,
  };
}
